import { useNavigate } from '@tanstack/react-router';
import {
  MessagesSquareIcon,
  PlusCircle,
} from 'lucide-react';
import { marked } from 'marked';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { CitationTooltipManager } from '@/components/CitationTooltip';
import type { TemplateChoiceSelection } from '@/components/case-partner/template-choices';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useCasePartner } from '@/components/contexts/CasePartnerContext';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import { DocumentSelectorDialog } from '@/components/document-selector';
import { Loading } from '@/components/shared';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { TokenLimitTooltip } from '@/components/shared/TokenLimitTooltip';
import { Button } from '@/components/ui/button';
import { chatHasVisibleRunningTurn } from '@/hooks/useChat';
import { DOCUMENT_ENRICHMENT_SECTIONS, type Document, useDocuments } from '@/hooks/useDocuments';
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures';
import useMatter from '@/hooks/useMatter';
import { useMatterMemory } from '@/hooks/useMatterMemory';
import { useTokenLimit } from '@/hooks/useTokenLimit';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import type { DocumentSelection } from '@/types';
import { stripCitationsFromContent, type WebCitationDisplay } from '@/utils/citation-utils';
import { freezeStreamingContinuityKey } from '@/utils/streaming-utils';
import { sanitizeHtml, sanitizeUserInput } from '../../src/utils/sanitization';
import type { QuickReplyAnswer } from './components';
import {
  ChatInputArea,
  buildQuotedPrompt,
  formatAskQuote,
  MessageCitedDocs,
  MessagesArea,
  SelectionAskButton,
} from './components';
// Extracted hooks
import { useFocusChatMessage } from './hooks/useFocusChatMessage';
import {
  buildLoadedChatSelections,
  createDocumentSelection,
  useAutoOpenProducedDraft,
  useCasePartnerScroll,
} from './hooks';
import { PromptFormDialog } from './PromptFormDialog';
import { PromptLibrary } from './PromptLibrary';

interface CasePartnerInterfaceProps {
  slug: string;
  documentsSearch?: string;
  docSelSearch?: string;
  newChatDraftRequest?: string;
  pendingQuery?: string;
  pendingWebSearch?: boolean;
  chatGuid?: string;
  onNewChat?: () => void;
  onNewChatDraftConsumed?: () => void;
}

export const CasePartnerInterface: React.FC<CasePartnerInterfaceProps> = ({
  slug,
  documentsSearch,
  docSelSearch,
  newChatDraftRequest,
  pendingQuery,
  pendingWebSearch,
  chatGuid,
  onNewChat,
  onNewChatDraftConsumed,
}) => {
  const navigate = useNavigate();
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const selectionAskContainerRef = useRef<HTMLDivElement>(null);
  const { selectedCase } = useCaseContext();
  const casePartner = useCasePartner();
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const loadedChatGuidRef = useRef<string | null>(null);
  const loadingChatGuidRef = useRef<string | null>(null);
  const previousRouteChatGuidRef = useRef<string | undefined>(undefined);
  const consumedDraftRequestRef = useRef<string | null>(null);
  const autoSendStartedRef = useRef(false);
  const [queuedQuery, setQueuedQuery] = useState<string | null>(null);
  const {
    contentRef: messagesContentRef,
    isAtBottom: isScrolledToBottom,
    isStreamingResponseBeyondFold,
    scrollRef: messagesScrollRef,
    scrollToBottom,
    scrollToResponseStart,
    stopScroll,
    streamingResponseRef,
  } = useCasePartnerScroll(casePartner.isStreamingCurrentChat, {
    guid: casePartner.currentChatGuid,
    isLoading: casePartner.isLoading,
    ready: casePartner.messages.length > 0,
    skip: Boolean(casePartner.focusMessage),
  });

  // Use global side panel
  const { openDocumentPanel, isOpen: isSidePanelOpen } = useSidePanel();

  // Get matter data
  const { matter } = useMatter(slug);

  // Get matter memory for the "overview updating" indicator
  const { isGenerating: isMatterMemoryGenerating } = useMatterMemory(slug);

  // Check token limits
  const { tokenLimitInfo } = useTokenLimit();

  // Use shared Case Partner state so the full page and side panel stay seamless.
  const {
    messages,
    chatHistory,
    isLoading,
    isStreamingCurrentChat,
    isCurrentChatTurnActive,
    canAbortStreaming,
    stopRequested,
    streamingChatGuid,
    sendMessage,
    streamingContent,
    streamingQuickReplyQuestions,
    streamingTemplateChoices,
    streamingThinkingSteps,
    streamingTextInsertions,
    streamingWaitMarkers,
    finalAnswerConfirmed,
    streamCatchingUp,
    turnStartedAt,
    turnWorkedDurationMs,
    abortStreaming,
    startNewChat,
    loadChat,
    suppressRouteChatLoadRef,
    currentChatGuid,
    focusMessage,
    isThinking,
  } = casePartner;

  const uiState = casePartner;
  const documentSelectionsHook = casePartner;
  const isComposingNewChat = uiState.isComposingNewChat;
  const isComposingNewChatRef = useRef(isComposingNewChat);
  const routeChatGuidRef = useRef(chatGuid);

  useEffect(() => {
    isComposingNewChatRef.current = isComposingNewChat;
  }, [isComposingNewChat]);

  useEffect(() => {
    routeChatGuidRef.current = chatGuid;
  }, [chatGuid]);

  const displayedCurrentChatGuid = isComposingNewChat ? null : currentChatGuid;
  const showChatLoading = Boolean(
    isLoading && displayedCurrentChatGuid && messages.length === 0 && !isComposingNewChat,
  );
  const showWelcome = !queuedQuery && !pendingQuery && !showChatLoading && (
    isComposingNewChat || (!displayedCurrentChatGuid && messages.length === 0)
  );
  const streamingContinuityKeyBase = `case-partner:${currentChatGuid ?? selectedCase ?? 'pending'}`;
  const streamingContinuityKeyRef = useRef(streamingContinuityKeyBase);
  streamingContinuityKeyRef.current = freezeStreamingContinuityKey(
    streamingContinuityKeyRef.current,
    streamingContinuityKeyBase,
    isStreamingCurrentChat,
  );
  const streamingContinuityKey = streamingContinuityKeyRef.current;
  const shouldShowHistoryThinkingDot = chatHistory.some((chat) => (
    chat.guid !== displayedCurrentChatGuid && chatHasVisibleRunningTurn(chat, streamingChatGuid)
  ));

  useAutoOpenProducedDraft({
    slug,
    producedDraftDocument: casePartner.latestProducedDraftDocument,
    onConsumed: casePartner.clearLatestProducedDraftDocument,
  });

  const timezone = getTimezone();

  // Use the custom hook for documents data
  const {
    documents,
    fetchedDocuments,
    isValidating: isDocumentsValidating,
    mutate,
  } = useDocuments(
    slug,
    undefined,
    undefined,
    undefined,
    {},
    timezone,
    // Case Partner only reads names, token counts and attachments (citations + selections),
    // so skip the enrichment sections to keep this whole-matter fetch cheap.
    { excludeSections: DOCUMENT_ENRICHMENT_SECTIONS },
  );

  const lastDocumentsQueryRef = useRef<string | undefined>(undefined);
  const [hydratedHandoffKey, setHydratedHandoffKey] = useState<string | null>(null);
  const documentsHandoffPending = Boolean(documentsSearch) && hydratedHandoffKey !== documentsSearch;

  useEffect(() => {
    if (!slug) return;

    casePartner.activate(slug as string);
    casePartner.setFullPageOpen(true);

    return () => {
      casePartner.setFullPageOpen(false);
    };
  }, [
    slug,
    casePartner.activate,
    casePartner.setFullPageOpen,
  ]);

  useFocusChatMessage(focusMessage, !isLoading && !isComposingNewChat, {
    scrollRef: messagesScrollRef,
    stopScroll,
  });

  useEffect(() => {
    // fetchedDocuments stays undefined until fetch-all succeeds, so citations are
    // not marked deleted against an empty list. A homepage document handoff also
    // holds them back until the forced revalidation settles.
    casePartner.setDocuments(documentsHandoffPending ? undefined : fetchedDocuments);
  }, [fetchedDocuments, documentsHandoffPending, casePartner.setDocuments]);

  useEffect(() => {
    casePartner.setDocumentsValidating(isDocumentsValidating || documentsHandoffPending);
  }, [isDocumentsValidating, documentsHandoffPending, casePartner.setDocumentsValidating]);

  useEffect(() => {
    casePartner.setRouterQueryDocuments(documentsSearch);
  }, [documentsSearch, casePartner.setRouterQueryDocuments]);

  useEffect(() => {
    casePartner.setRouterQueryDocSel(docSelSearch);
  }, [docSelSearch, casePartner.setRouterQueryDocSel]);

  useEffect(() => {
    if (chatGuid || documentsSearch || isComposingNewChatRef.current) return;
    if (messages.length > 0 || isStreamingCurrentChat) return;

    documentSelectionsHook.clearSelections();
    uiState.setShowAllDocuments(false);
    uiState.setShowPromptSuggestions(false);
    uiState.setHasShownInitialSuggestions(false);
  }, [
    chatGuid,
    documentsSearch,
    messages.length,
    isStreamingCurrentChat,
    documentSelectionsHook.clearSelections,
    uiState.setHasShownInitialSuggestions,
    uiState.setShowAllDocuments,
    uiState.setShowPromptSuggestions,
  ]);

  // Force revalidation when opening Case Partner with pre-selected documents
  // This ensures newly uploaded documents are fetched even if within SWR's deduplication window
  useEffect(() => {
    if (!documentsSearch) {
      lastDocumentsQueryRef.current = undefined;
      setHydratedHandoffKey(null);
      return;
    }

    const documentsQueryChanged = lastDocumentsQueryRef.current !== documentsSearch;
    if (!documentsQueryChanged) return;

    lastDocumentsQueryRef.current = documentsSearch;
    void mutate(undefined, { revalidate: true }).finally(() => {
      setHydratedHandoffKey(documentsSearch);
    });
  }, [documentsSearch, mutate]);

  const [addPromptDialogOpen, setAddPromptDialogOpen] = useState(false);
  const [addPromptPrefill, setAddPromptPrefill] = useState('');

  const restoreLoadedChatState = useCallback((result: {
    documentSelections: DocumentSelection[];
    lastUserWebSearch?: boolean;
  }) => {
    uiState.setWebSearchEnabled(Boolean(result.lastUserWebSearch));
    documentSelectionsHook.setDocumentSelections(
      buildLoadedChatSelections(result.documentSelections, documents),
    );
    uiState.setHasShownInitialSuggestions(true);
  }, [
    documents,
    documentSelectionsHook.setDocumentSelections,
    uiState.setWebSearchEnabled,
    uiState.setHasShownInitialSuggestions,
  ]);

  // Load the current chat when the component mounts and chatGuid changes
  useEffect(() => {
    const routeChatChanged = previousRouteChatGuidRef.current !== chatGuid;
    previousRouteChatGuidRef.current = chatGuid;

    if (!chatGuid || !selectedCase) {
      loadedChatGuidRef.current = null;
      loadingChatGuidRef.current = null;
      return;
    }

    if (isComposingNewChat || isComposingNewChatRef.current || suppressRouteChatLoadRef.current) {
      loadingChatGuidRef.current = null;
      return;
    }

    // replaceState leaves the matched route on the previous chat. Skip that stale
    // param, but still load when the router itself navigates to a different chat.
    if (
      currentChatGuid &&
      currentChatGuid !== chatGuid &&
      !routeChatChanged
    ) {
      return;
    }

    const hasLoadedRouteChat = loadedChatGuidRef.current === chatGuid;
    // A live stream already owns this transcript. Reloading it (the effect
    // re-runs whenever `loadChat` is recreated) replaces messages and looks
    // like the page refreshed to the latest persisted state.
    if (
      currentChatGuid === chatGuid &&
      (isStreamingCurrentChat || messages.length > 0)
    ) {
      loadedChatGuidRef.current = chatGuid;
      return;
    }
    if ((!hasLoadedRouteChat || chatGuid !== currentChatGuid) && loadingChatGuidRef.current !== chatGuid) {
      loadingChatGuidRef.current = chatGuid;
      loadChat(chatGuid, { force: !hasLoadedRouteChat })
        .then((result) => {
          if (
            isComposingNewChatRef.current
            || suppressRouteChatLoadRef.current
            || routeChatGuidRef.current !== chatGuid
          ) {
            return;
          }

          loadedChatGuidRef.current = chatGuid;

          if (result) {
            restoreLoadedChatState(result);
          }
        })
        .catch((error) => {
          if (
            isComposingNewChatRef.current
            || suppressRouteChatLoadRef.current
            || routeChatGuidRef.current !== chatGuid
          ) {
            return;
          }

          console.error('Error loading chat:', error);
          if (error?.response?.status === 404 && slug) {
            navigate({
              to: '/matter/$slug/case-partner',
              params: { slug },
              resetScroll: false,
            });
            return;
          }
          toast.error(apiErrorMessage(error) || 'Failed to load chat');
        })
        .finally(() => {
          if (loadingChatGuidRef.current === chatGuid) {
            loadingChatGuidRef.current = null;
          }
        });
    }
  }, [
    chatGuid,
    currentChatGuid,
    isComposingNewChat,
    isStreamingCurrentChat,
    messages.length,
    selectedCase,
    loadChat,
    navigate,
    slug,
    restoreLoadedChatState,
  ]);

  useEffect(() => {
    if (!slug) return;
    if (chatGuid && currentChatGuid && chatGuid !== currentChatGuid) return;
    const path = currentChatGuid
      ? `/matter/${slug}/case-partner/${currentChatGuid}`
      : `/matter/${slug}/case-partner`;
    if (window.location.pathname === path) return;
    History.prototype.replaceState.call(
      window.history,
      window.history.state,
      '',
      `${path}${window.location.search}`,
    );
  }, [chatGuid, currentChatGuid, slug]);

  // Keyboard shortcut for sidebar toggle
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        uiState.setIsSidebarVisible((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [uiState.setIsSidebarVisible]);

  // Fetch prompt suggestions
  const fetchPromptSuggestions = useCallback(async (documentGuids: string[]) => {
    if (!documentGuids || documentGuids.length === 0) {
      uiState.setPromptSuggestions([]);
      return;
    }

    try {
      const response = await backendClient.post('/case-partner/prompt-suggestions', documentGuids, {
        params: slug ? { matter_guid: slug } : {},
      });
      if (response.data?.suggestions) {
        uiState.setPromptSuggestions(response.data.suggestions);
      }
    } catch (error) {
      console.error('Error fetching prompt suggestions:', error);
      uiState.setPromptSuggestions([]);
    }
  }, [slug, uiState.setPromptSuggestions]);

  // Effect to fetch suggestions when documents change
  useEffect(() => {
    const parentGuids = Array.from(documentSelectionsHook.documentSelections.keys());
    if (parentGuids.length > 0) {
      fetchPromptSuggestions(parentGuids);
    } else {
      uiState.setPromptSuggestions([]);
    }
  }, [documentSelectionsHook.documentSelections, fetchPromptSuggestions, uiState.setPromptSuggestions]);

  // Effect to show suggestions
  useEffect(() => {
    if (messages.length === 0 &&
        !uiState.hasShownInitialSuggestions &&
        documentSelectionsHook.documentSelections.size > 0 &&
        uiState.promptSuggestions.length > 0 &&
        !uiState.showDocumentSelector &&
        uiState.userInput.length === 0) {

      if (document.activeElement !== chatInputRef.current) {
        chatInputRef.current?.focus();
      }

      uiState.setShowPromptSuggestions(true);
      uiState.setHasShownInitialSuggestions(true);
    }
  }, [
    uiState.promptSuggestions,
    documentSelectionsHook.documentSelections,
    uiState.showDocumentSelector,
    uiState.userInput,
    messages.length,
    uiState.hasShownInitialSuggestions,
    uiState.setShowPromptSuggestions,
    uiState.setHasShownInitialSuggestions,
  ]);

  const handleStartNewChat = useCallback((options?: { skipNavigate?: boolean; preserveDocuments?: boolean }) => {
    if (isComposingNewChatRef.current && !options?.skipNavigate && !options?.preserveDocuments) {
      onNewChat?.();
      return;
    }

    isComposingNewChatRef.current = true;
    if (!options?.preserveDocuments) {
      casePartner.setRouterQueryDocuments(undefined);
      casePartner.setRouterQueryDocSel(undefined);
      documentSelectionsHook.clearSelections();
    }
    uiState.setUserInput('');
    uiState.setComposerQuote(null);
    uiState.setShowAllDocuments(false);
    uiState.setShowPromptSuggestions(false);
    uiState.setHasShownInitialSuggestions(false);

    startNewChat();
    uiState.setIsComposingNewChat(true);
    uiState.resetWebSearchToDefault();

    if (!options?.skipNavigate) {
      onNewChat?.();
    }
    setTimeout(() => chatInputRef.current?.focus(), 0);
  }, [
    casePartner.setRouterQueryDocuments,
    casePartner.setRouterQueryDocSel,
    startNewChat,
    onNewChat,
    documentSelectionsHook.clearSelections,
    uiState.setUserInput,
    uiState.setComposerQuote,
    uiState.setShowAllDocuments,
    uiState.setShowPromptSuggestions,
    uiState.setHasShownInitialSuggestions,
    uiState.setIsComposingNewChat,
    uiState.resetWebSearchToDefault,
  ]);

  useEffect(() => {
    if (!newChatDraftRequest && !pendingQuery) return;
    const requestKey = newChatDraftRequest ?? `q:${pendingQuery}`;
    if (consumedDraftRequestRef.current === requestKey) return;

    consumedDraftRequestRef.current = requestKey;
    autoSendStartedRef.current = false;
    const queryToSend = pendingQuery?.trim() || null;
    handleStartNewChat({
      skipNavigate: Boolean(queryToSend),
      preserveDocuments: Boolean(queryToSend && documentsSearch),
    });
    if (queryToSend) {
      if (pendingWebSearch !== undefined) {
        uiState.setWebSearchEnabled(pendingWebSearch);
      }
      uiState.setUserInput(queryToSend);
      setQueuedQuery(queryToSend);
      return;
    }
    onNewChatDraftConsumed?.();
  }, [
    newChatDraftRequest,
    pendingQuery,
    pendingWebSearch,
    documentsSearch,
    handleStartNewChat,
    onNewChatDraftConsumed,
    uiState.setUserInput,
    uiState.setWebSearchEnabled,
  ]);

  const { isEnabled: webResearchEnabled, settings: webResearchSettings } = useFeatures(FEATURE_FLAGS.WEB_RESEARCH);
  const webSearchAvailable = webResearchEnabled && webResearchSettings.configured !== false;

  const sendCasePartnerMessage = useCallback(async (
    rawInput: string,
    clearComposerInput: boolean,
    userMessageMetadata?: Record<string, unknown>,
  ) => {
    const quote = uiState.composerQuote;
    const quotedInput = quote
      ? buildQuotedPrompt(quote, rawInput)
      : rawInput;
    if (
      !quotedInput.trim() ||
      (isCurrentChatTurnActive && !isComposingNewChat)
    ) return;

    const sanitizedInput = sanitizeUserInput(quotedInput);
    if (!sanitizedInput.trim()) {
      if (clearComposerInput) {
        uiState.setUserInput('');
      }
      return;
    }

    const messageToBeSent = sanitizedInput;
    const selectionsForBackend = documentSelectionsHook.getDocumentSelectionsForBackend();

    if (clearComposerInput) {
      uiState.setUserInput('');
      uiState.setComposerQuote(null);
    }
    uiState.setIsComposingNewChat(false);
    uiState.setIsSidebarVisible(false);
    scrollToResponseStart();

    try {
      const result = await sendMessage(messageToBeSent, selectionsForBackend, {
        forceNewChat: isComposingNewChat,
        userMessageMetadata: {
          ...userMessageMetadata,
          ...(quote && clearComposerInput
            ? {
                quoted_excerpt: quote,
                quoted_question: rawInput.trim() || undefined,
              }
            : {}),
        },
        webSearch: webSearchAvailable && uiState.webSearchEnabled,
      });
      if (result?.conflict && clearComposerInput) {
        uiState.setUserInput(rawInput);
        uiState.setComposerQuote(quote);
      }
    } finally {
      isComposingNewChatRef.current = false;
    }
  }, [
    uiState.setUserInput,
    uiState.setIsComposingNewChat,
    uiState.setIsSidebarVisible,
    isCurrentChatTurnActive,
    isComposingNewChat,
    documentSelectionsHook.getDocumentSelectionsForBackend,
    scrollToResponseStart,
    sendMessage,
    uiState.webSearchEnabled,
    webSearchAvailable,
    uiState.composerQuote,
    uiState.setComposerQuote,
  ]);

  const handleSendMessage = useCallback(async () => {
    await sendCasePartnerMessage(uiState.userInput, true);
  }, [sendCasePartnerMessage, uiState.userInput]);

  useEffect(() => {
    if (!queuedQuery) return;
    if (!isComposingNewChat) return;
    if (selectedCase !== slug) return;
    if (autoSendStartedRef.current) return;
    // Wait until URL document selections have hydrated before auto-sending.
    if (
      documentsSearch &&
      documentSelectionsHook.urlDocumentHydrationStatus !== 'ready'
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (autoSendStartedRef.current) return;
      autoSendStartedRef.current = true;
      const query = queuedQuery;
      setQueuedQuery(null);
      // Fire-and-forget: sendMessage waits for the full stream. Navigating to
      // /case-partner/$chatGuid after that remounts this page and reloads the
      // transcript. Stay on this route and just drop draft/q from the URL.
      void sendCasePartnerMessage(query, true);
      onNewChatDraftConsumed?.();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    queuedQuery,
    isComposingNewChat,
    selectedCase,
    slug,
    documentsSearch,
    documentSelectionsHook.urlDocumentHydrationStatus,
    sendCasePartnerMessage,
    onNewChatDraftConsumed,
  ]);

  const handleQuickReplySubmit = useCallback((value: string, answers: QuickReplyAnswer[]) => {
    void sendCasePartnerMessage(value, false, {
      quick_reply_answers: { answers },
    });
  }, [sendCasePartnerMessage]);

  const handleTemplateChoiceSubmit = useCallback((value: string, selection: TemplateChoiceSelection) => {
    void sendCasePartnerMessage(value, false, {
      template_choice: selection,
    });
  }, [sendCasePartnerMessage]);

  const handleOpenDocument = useCallback((docGuid: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (docGuid) {
      openDocumentPanel({
        documentGuid: docGuid,
        activeTab: 'document',
      });
    }
  }, [openDocumentPanel]);

  const copyToClipboard = useCallback((content: string, messageId: string | number | undefined) => {
    const msgId = messageId?.toString() || '';
    if (!msgId) return;

    try {
      const cleanContent = stripCitationsFromContent(content, true);
      const htmlContent = marked(cleanContent, { gfm: true, breaks: true });
      const sanitizedHtml = sanitizeHtml(htmlContent as string);

      const tempElement = document.createElement('div');
      tempElement.style.position = 'absolute';
      tempElement.style.left = '-9999px';
      tempElement.innerHTML = sanitizedHtml;
      document.body.appendChild(tempElement);

      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(tempElement);

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
        document.body.removeChild(tempElement);

        uiState.setCopiedMessageIds((prev) => {
          const updated = new Set(prev);
          updated.add(msgId);
          return updated;
        });

        setTimeout(() => {
          uiState.setCopiedMessageIds((prev) => {
            const updated = new Set(prev);
            updated.delete(msgId);
            return updated;
          });
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy formatted text:', err);
      navigator.clipboard.writeText(content).catch(console.error);
    }
  }, [uiState.setCopiedMessageIds]);

  // Handle dialog close with focus management
  const handleAskAboutSelection = useCallback((text: string) => {
    uiState.setComposerQuote(formatAskQuote(text));
    window.setTimeout(() => chatInputRef.current?.focus(), 0);
  }, [uiState.setComposerQuote]);

  const handleDocumentSelectorClose = useCallback(() => {
    setTimeout(() => {
      chatInputRef.current?.focus();

      if (messages.length === 0 &&
          !uiState.hasShownInitialSuggestions &&
          documentSelectionsHook.documentSelections.size > 0 &&
          uiState.promptSuggestions.length > 0 &&
          uiState.userInput.length === 0) {
        uiState.setShowPromptSuggestions(true);
        uiState.setHasShownInitialSuggestions(true);
      }
    }, 500);
  }, [
    messages.length,
    uiState.hasShownInitialSuggestions,
    documentSelectionsHook.documentSelections,
    uiState.promptSuggestions,
    uiState.userInput,
    uiState.setShowPromptSuggestions,
    uiState.setHasShownInitialSuggestions,
  ]);

  // Handle upload complete
  // TODO: Refactor inefficient handleUploadComplete in CasePartnerInterface to not fetch and scan all documents.
  const handleUploadComplete = useCallback(async (uploadedFileNames?: string[]) => {
    if (!uploadedFileNames || uploadedFileNames.length === 0) return;

    try {
      // Refresh documents via SWR mutate - returns transformed data
      const allDocs = await mutate();

      if (allDocs && allDocs.length > 0) {
        const newlyUploadedDocs = allDocs.filter((doc: Document) => {
          return uploadedFileNames.some((fileName) => {
            const baseName = fileName.split(/[/\\]/).pop() || '';
            return doc.fileName?.includes(baseName);
          });
        });

        if (newlyUploadedDocs.length > 0) {
          documentSelectionsHook.setDocumentSelections((prev) => {
            const newMap = new Map(prev);

            for (const doc of newlyUploadedDocs) {
              if (!doc.guid || newMap.has(doc.guid)) continue;
              newMap.set(doc.guid, createDocumentSelection(doc));
            }

            return newMap;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching updated documents:', error);
      uiState.setIsUploading(false);
    }
  }, [mutate, documentSelectionsHook, uiState]);

  const renderCitedDocs = useCallback((
    content: string,
    webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>,
  ) => (
    <MessageCitedDocs
      content={content}
      documents={fetchedDocuments}
      slug={slug}
      webCitationMeta={webCitationMeta}
    />
  ), [fetchedDocuments, slug]);

  const handleSaveToLibrary = useCallback((content: string) => {
    setAddPromptPrefill(content);
    setAddPromptDialogOpen(true);
  }, []);

  return (
    <CitationTooltipManager>
      <div className="h-full">
        {typeof selectedCase === 'undefined' ? (
          <NoCaseSelected />
        ) : (
          <div className="relative flex h-full overflow-hidden bg-white">
            {/* Main chat area */}
            <div
              className={cn(
                'min-w-0 flex-1 overflow-hidden bg-white',
                uiState.isSidebarVisible && 'xl:pr-12 2xl:pr-20',
              )}
            >
              <div ref={selectionAskContainerRef} className="relative flex h-full flex-col overflow-auto">
                <SelectionAskButton
                  containerRef={selectionAskContainerRef}
                  onAsk={handleAskAboutSelection}
                />
                {/* Fixed position buttons */}
                <div className={`absolute top-5 z-20 flex ${
                  isSidePanelOpen ? 'flex-col space-y-2 left-0' : 'flex-row space-x-2 left-5'
                }`}>
                  {!uiState.isSidebarVisible && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative h-10 w-10 rounded-full bg-white text-slate-700 hover:bg-slate-50"
                    onClick={() => uiState.setIsSidebarVisible(true)}
                    title="View chat history"
                  >
                    <span className="relative inline-flex">
                      <MessagesSquareIcon className="h-5 w-5" />
                      {shouldShowHistoryThinkingDot && (
                        <span
                          aria-hidden="true"
                          className="-right-1 -top-1 absolute h-2.5 w-2.5 animate-pulse rounded-full bg-[#8FA0D0] ring-2 ring-white"
                        />
                      )}
                    </span>
                  </Button>
                  )}

                  {!uiState.isSidebarVisible && !isComposingNewChat && messages.length > 0 && (
                    <TokenLimitTooltip
                      message={tokenLimitInfo.message}
                      disabled={!tokenLimitInfo.isWithinLimit}
                      side="right"
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-full bg-white text-slate-700 hover:bg-slate-50"
                        onClick={() => handleStartNewChat()}
                        title="Start a new chat"
                        disabled={!tokenLimitInfo.isWithinLimit}
                      >
                        <PlusCircle className="h-5 w-5" />
                      </Button>
                    </TokenLimitTooltip>
                  )}
                </div>

                {/* Document selector dialog */}
                <DocumentSelectorDialog
                  open={uiState.showDocumentSelector}
                  onOpenChange={uiState.setShowDocumentSelector}
                  matterSlug={slug}
                  documentSelections={documentSelectionsHook.documentSelections}
                  onToggleSelection={documentSelectionsHook.toggleDocumentSelection}
                  onClearSelection={documentSelectionsHook.clearSelections}
                  onUploadComplete={handleUploadComplete}
                  onDialogClose={handleDocumentSelectorClose}
                  chatInputRef={chatInputRef}
                />

                {showWelcome ? (
                  <div className="flex-1 relative">
                    <div className="absolute inset-0 flex flex-col items-center justify-center -mt-10">
                      <div className="flex flex-col items-center">
                        <div className="bg-blue-50 p-4 rounded-full mb-4">
                          <img src="/logo-icon.svg" className="w-12 h-12" alt="Crimson AI" />
                        </div>
                        <h2 className="text-xl font-semibold text-slate-800 mb-1">How can I assist?</h2>
                        <p className="text-slate-500 text-center max-w-md">
                          {ui_labels.case_partner_empty_hint}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : showChatLoading ? (
                  <div className="flex flex-1 items-center justify-center">
                    <Loading />
                  </div>
                ) : (
                  <MessagesArea
                    messages={messages}
                    streamingContent={isStreamingCurrentChat ? streamingContent : ''}
                    streamingQuickReplyQuestions={isStreamingCurrentChat ? streamingQuickReplyQuestions : []}
                    streamingTemplateChoices={isStreamingCurrentChat ? streamingTemplateChoices : null}
                    streamingThinkingSteps={isStreamingCurrentChat ? streamingThinkingSteps : []}
                    streamingTextInsertions={isStreamingCurrentChat ? streamingTextInsertions : []}
                    streamingWaitMarkers={isStreamingCurrentChat ? streamingWaitMarkers : []}
                    isStreaming={isStreamingCurrentChat}
                    catchingUp={isStreamingCurrentChat && streamCatchingUp}
                    finalAnswerConfirmed={isStreamingCurrentChat && finalAnswerConfirmed}
                    turnStartedAt={isStreamingCurrentChat ? turnStartedAt : null}
                    reasoningDurationMs={isStreamingCurrentChat ? turnWorkedDurationMs : null}
                    isThinking={isStreamingCurrentChat && isThinking}
                    stopRequested={isStreamingCurrentChat && stopRequested}
                    isLoading={isLoading}
                    documents={fetchedDocuments}
                    matterGuid={matter?.guid || selectedCase}
                    chatGuid={currentChatGuid}
                    slug={slug}
                    copiedMessageIds={uiState.copiedMessageIds}
                    onCopyMessage={copyToClipboard}
                    onSaveToLibrary={handleSaveToLibrary}
                    scrollAreaRef={messagesScrollRef}
                    contentRef={messagesContentRef}
                    isScrolledToBottom={isScrolledToBottom}
                    isStreamingResponseBeyondFold={isStreamingResponseBeyondFold}
                    onScrollToBottom={() => {
                      scrollToBottom({ animation: 'smooth' });
                    }}
                    renderCitedDocs={renderCitedDocs}
                    streamingContinuityKey={streamingContinuityKey}
                    streamingResponseRef={streamingResponseRef}
                    quickReplyDisabled={isCurrentChatTurnActive && !isComposingNewChat}
                    onQuickReplySubmit={handleQuickReplySubmit}
                    templateChoiceDisabled={isCurrentChatTurnActive && !isComposingNewChat}
                    onTemplateChoiceSubmit={handleTemplateChoiceSubmit}
                  />
                )}

                <div className="px-6 pt-1 md:px-8">
                  <div className="relative mx-auto max-w-5xl">
                    <ChatInputArea
                      inputRef={chatInputRef}
                      userInput={uiState.userInput}
                      onInputChange={uiState.setUserInput}
                      onSend={handleSendMessage}
                      onAbort={abortStreaming}
                      isStreaming={isCurrentChatTurnActive && !isComposingNewChat}
                      canAbortStreaming={canAbortStreaming}
                      isUploading={uiState.isUploading}
                      webSearchEnabled={uiState.webSearchEnabled}
                      onWebSearchChange={uiState.setWebSearchEnabled}
                      showWebSearchToggle={webSearchAvailable}
                      tokenLimitInfo={tokenLimitInfo}
                      documentSelections={documentSelectionsHook.documentSelections}
                      showAllDocuments={uiState.showAllDocuments}
                      onShowAllDocuments={() => uiState.setShowAllDocuments(true)}
                      onAttachmentClick={() => uiState.setShowDocumentSelector(true)}
                      onOpenDocument={handleOpenDocument}
                      onRemoveDocument={documentSelectionsHook.removeDocumentGroup}
                      onToggleParent={documentSelectionsHook.toggleParentInclusion}
                      onToggleAttachment={documentSelectionsHook.toggleAttachmentInclusion}
                      onClearAllDocuments={() => {
                        documentSelectionsHook.clearSelections();
                        uiState.setShowAllDocuments(false);
                      }}
                      onPromptLibraryClick={() => uiState.setShowPromptLibrary(true)}
                      matterGuid={slug}
                      chatGuid={displayedCurrentChatGuid}
                      quotedExcerpt={uiState.composerQuote}
                      onClearQuote={() => uiState.setComposerQuote(null)}
                      promptSuggestions={uiState.promptSuggestions}
                      showPromptSuggestions={uiState.showPromptSuggestions}
                      onSuggestionClick={(value) => {
                        uiState.setUserInput(value);
                        uiState.setShowPromptSuggestions(false);
                      }}
                      onCloseSuggestions={() => uiState.setShowPromptSuggestions(false)}
                      isMatterMemoryGenerating={isMatterMemoryGenerating}
                      variant={showWelcome ? 'welcome' : 'conversation'}
                    />

                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex-1 text-center">
                        <p className="text-xs text-slate-400">
                          Case Partner can make mistakes. Verify the outputs.
                        </p>
                      </div>
                      {uiState.userInput.length > 80000 && (
                        <p className="text-xs text-amber-600">
                          {uiState.userInput.length.toLocaleString()} / 100,000
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Prompt Library Dialog */}
        <PromptLibrary
          open={uiState.showPromptLibrary}
          onOpenChange={uiState.setShowPromptLibrary}
          onSelectPrompt={(prompt) => {
            uiState.setUserInput(prompt);
            setTimeout(() => chatInputRef.current?.focus(), 100);
          }}
        />

        {/* Add Prompt Dialog (opened from message save button) */}
        <PromptFormDialog
          open={addPromptDialogOpen}
          onOpenChange={setAddPromptDialogOpen}
          prefillContent={addPromptPrefill}
        />
      </div>
    </CitationTooltipManager>
  );
};

export default CasePartnerInterface;
